/**
 * Category slice (SCRUM-1953, RTK bundle 4) — the former switch reducer
 * categoryReducer.js as createSlice. Behaviour-preserving: every case answers
 * the same legacy action type string (categoryTypes.js) that categoryAction.ts
 * already dispatches, and returns the same state. The actions module is
 * unchanged, so the pending-change (SCRUM-1627) and overlay-slug (SCRUM-1703)
 * resolve/reject contracts the category screens branch on stay verbatim and no
 * importer changes. The five cases that change nothing still return a new,
 * equal object, as the switch did. `CLEAR_MESSAGE` is the same string
 * productproperties answers to; each slice clears its own message.
 * Characterised by tests/integration/reducers/catalogueReducers.characterisation.test.js.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  POST_SUBCATEGORY,
  POST_CATEGORY,
  GET_CATEGORIES,
  UPDATE_CATEGORY,
  UPDATE_SUBCATEGORY,
  DELETE_CATEGORY,
  DELETE_SUBCATEGORY,
  GET_PAGE_TYPE_OPTIONS,
  GET_SITE_CATEGORIES,
  GET_CATEGORIES_TO_ADD,
  ADD_SITE_CAT,
  DELETE_SITE_CAT,
  CLEAR_MESSAGE,
  PATCH_CATEGORY,
  CLEAR_SUBCATEGORIES,
  GET_SUBCATEGORY_BY_ID,
} from "./categoryTypes";
import type { MessageResponse } from "../../../../shared/apiTypes";

type CategoryState = {
  /** all categories */
  categories: any[];
  /** site categories */
  siteCategories: any[];
  /** categories you can add to a site */
  categories_toadd: any[];
  // SCRUM-1653: the picker's subcategories (with product wrappers), one response
  pageTypeSubcategories: any[];
  /** to get success message */
  message: string;
  subCategory_by_id: any;
};

/** A write that answers with the server message, under its own action type. */
type MessageAction<T extends string> = PayloadAction<MessageResponse, T>;

const initialState: CategoryState = {
  categories: [],
  siteCategories: [],
  categories_toadd: [],
  pageTypeSubcategories: [],
  message: "", // to get success message
  subCategory_by_id: {},
};

/** The server message a write answers with. */
const storeMessage = (state: CategoryState, action: { payload: MessageResponse }): CategoryState => ({ ...state, message: action.payload.msg });

/** A case that changes nothing but, like the switch, returns a new object. */
const copy = (state: CategoryState): CategoryState => ({ ...state });

const categorySlice = createSlice({
  name: "categories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(CLEAR_MESSAGE, (state) => ({ ...state, message: "" }))
      /*********************** CATEGORY ************************/
      .addCase(GET_CATEGORIES, (state, action: PayloadAction<CategoryState["categories"], typeof GET_CATEGORIES>) => ({ ...state, categories: action.payload }))
      .addCase<typeof POST_CATEGORY, MessageAction<typeof POST_CATEGORY>>(POST_CATEGORY, storeMessage)
      .addCase<typeof UPDATE_CATEGORY, MessageAction<typeof UPDATE_CATEGORY>>(UPDATE_CATEGORY, storeMessage)
      .addCase(DELETE_CATEGORY, copy)
      /*********************** SUBCATEGORY ************************/
      .addCase<typeof POST_SUBCATEGORY, MessageAction<typeof POST_SUBCATEGORY>>(POST_SUBCATEGORY, storeMessage)
      .addCase<typeof UPDATE_SUBCATEGORY, MessageAction<typeof UPDATE_SUBCATEGORY>>(UPDATE_SUBCATEGORY, storeMessage)
      .addCase(DELETE_SUBCATEGORY, copy)
      .addCase(GET_PAGE_TYPE_OPTIONS, (state, action: PayloadAction<unknown, typeof GET_PAGE_TYPE_OPTIONS>) => ({
        ...state,
        pageTypeSubcategories: Array.isArray(action.payload) ? action.payload : [],
      }))
      .addCase(GET_SUBCATEGORY_BY_ID, (state, action: PayloadAction<CategoryState["subCategory_by_id"], typeof GET_SUBCATEGORY_BY_ID>) => ({
        ...state,
        subCategory_by_id: action.payload,
      }))
      .addCase(CLEAR_SUBCATEGORIES, (state) => ({ ...state, pageTypeSubcategories: [] }))
      /******************************** AFFECTATION  ********************************/
      .addCase(GET_CATEGORIES_TO_ADD, (state, action: PayloadAction<CategoryState["categories_toadd"], typeof GET_CATEGORIES_TO_ADD>) => ({
        ...state,
        categories_toadd: action.payload,
      }))
      .addCase(ADD_SITE_CAT, copy)
      .addCase(GET_SITE_CATEGORIES, (state, action: PayloadAction<CategoryState["siteCategories"], typeof GET_SITE_CATEGORIES>) => ({
        ...state,
        siteCategories: action.payload,
      }))
      .addCase(DELETE_SITE_CAT, copy)
      .addCase(PATCH_CATEGORY, copy);
  },
});

export default categorySlice.reducer;
